import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ze } from 'utils/zhEn';
import { Select, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { actionFetchUserSceneTagOptions } from 'reducers/summary/summaryActions';
import { DashboardConst } from 'utils/constants/dashboard-const';
import { CsSelect } from 'components/antd-extension/CsSelect';
import { selectCurrentAppCustomUserSceneTagLabel } from 'utils/selectors/selectors';
import { isNotNullish } from 'utils/nullish';
import { CS_STYLES } from 'utils/constants/style-constants';

const UserSceneTagSelect = ({
  value,
  isMultiple,
  /** 是否在选项列表最上方显示"全部XX合计"选项（value为USER_SCENE_TAG_TOTAL_OF_ALL_TAGS） */
  showTotalOfAllTags = false,
  style,
  onChangeWhenDropdownClosed = () => {},
}) => {
  const dispatch = useDispatch();
  const userSceneTagOptionsInfoImmutable = useSelector(reduxState => reduxState.summary.getIn(['selectOptions', 'userSceneTag']));
  const { userSceneTagOptions, isFetching } = useMemo(() => {
    const info = userSceneTagOptionsInfoImmutable?.toJS() ?? {};
    return { userSceneTagOptions: info.options ?? [], isFetching: info.isFetching ?? false };
  }, [userSceneTagOptionsInfoImmutable]);

  const customUserSceneTagLabel = useSelector(selectCurrentAppCustomUserSceneTagLabel);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [intermediateValue, setIntermediateValue] = useState(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    setIntermediateValue(value);
  }, [value]);

  useEffect(() => {
    const hasValue = Array.isArray(value) ? value.length > 0 : (isNotNullish(value) && value !== '');
    if (hasValue && !dropdownOpen) {
      dispatch(actionFetchUserSceneTagOptions(60000));
    }
  }, [value]);

  const labelSingular = customUserSceneTagLabel || ze('场景', 'Scene');
  const labelPlural = customUserSceneTagLabel || ze('场景', 'Scenes');
  const placeHolder = ze(`不区分${labelSingular}`, `All ${labelPlural}`);

  const finalOptions = useMemo(() => {
    if (!showTotalOfAllTags) {
      return userSceneTagOptions;
    }
    const totalOption = {
      label: <span style={{ color: CS_STYLES.getPrimaryColor() }}>{ ze(`全部${labelPlural}合计`, `All ${labelPlural}`) }</span>,
      value: DashboardConst.USER_SCENE_TAG_TOTAL_OF_ALL_TAGS,
    };
    return [totalOption, ...userSceneTagOptions];
  }, [showTotalOfAllTags, userSceneTagOptions, labelPlural]);

  const dom = <CsSelect
    showSearch={true}
    optionFilterProp="label"
    allowClear={false}
    placeholder={placeHolder}
    style={{ width: '160px', ...style }}
    {...(isMultiple ? { mode: 'multiple' } : {})}
    value={intermediateValue}
    options={finalOptions}
    showArrow={true}
    dropdownRender={(originNode) => {
      return <Spin
        spinning={isFetching}
      >{ originNode }</Spin>;
    }}
    onChange={(v) => {
      if (dropdownOpen && isMultiple) {
        setIntermediateValue(v);
      } else {
        onChangeWhenDropdownClosed(v);
      }
    }}
    onSearch={(v) => {
      setSearchText(v);
    }}
    onKeyDown={(e) => {
      if (e.key !== 'Enter') {
        return;
      }
      // 可以通过按下回车键直接确认选择一个任意字串作为场景标签的逻辑
      const newItem = searchText;
      setSearchText('');
      if (newItem) {
        if (isMultiple) {
          // 多选模式：添加到现有选项中（避免重复）
          if (!intermediateValue?.includes(newItem)) {
            const newValue = [...(intermediateValue || []), newItem];
            if (dropdownOpen) {
              setIntermediateValue(newValue);
            } else {
              onChangeWhenDropdownClosed(newValue);
            }
          }
        } else {
          // 单选模式：直接设置为新值
          onChangeWhenDropdownClosed(newItem);
        }
      }
    }}
    onDropdownVisibleChange={(open) => {
      setDropdownOpen(open);
      if (open) {
        dispatch(actionFetchUserSceneTagOptions(60000));
        setIntermediateValue(value);
      } else if (value !== intermediateValue) {
        onChangeWhenDropdownClosed(intermediateValue);
      }
    }}
  />;

  return dom;
};

export default UserSceneTagSelect;
